import { AxiosRequestConfig } from 'axios'

import Base from './base'
import { withAutoCancellation } from '../services/request'
import { Metadata, Type } from '../types/filters'
import { Snippet, Query, FilterOwners } from '../types/snippet'
import { addBreadcrumb } from '../sentry-config'

type SnippetsResponse = {
  data: Snippet[]
  metadata: Metadata
}

export default class Snippets extends Base {
  DEFAULT_PARAMS: Query = {
    per_page: 100,
    page: 1,
    sort_direction: 'ASC',
    include_paging_counts: true,
  }
  PERSONAL_SNIPPETS_PATH = 'searches/snippets'
  TEAM_SNIPPETS_PATH = 'searches/team_snippets'

  index(path: string, params: Query, config?: AxiosRequestConfig): Promise<SnippetsResponse> {
    addBreadcrumb({ message: 'Scintillas: Getting Snippets List', category: 'request' })
    return this.api.post(path, params, config)
  }

  show(snippetId: number): Promise<Snippet> {
    addBreadcrumb({ message: 'Scintillas: Getting Snippet by ID', category: 'request' })
    return this.api.get(`snippets/${snippetId}`)
  }

  updateCounts(snippetId: number): Promise<unknown> {
    addBreadcrumb({
      message: 'Scintillas: Request to increment the number of counts used.',
      category: 'request',
    })
    return this.api.put(`snippets_counts/${snippetId}`, {
      counter_name: 'used',
    })
  }

  async getSnippets(params: Query, path: string, config?: AxiosRequestConfig): Promise<Snippet[]> {
    const snippetsJSON = await this.index(path, params, config)

    if (!snippetsJSON) {
      return []
    }

    const snippetsList = snippetsJSON.data
    const totalPages = snippetsJSON.metadata.paging.total_pages || 1
    const requests = []
    if (totalPages < 2) {
      return snippetsList
    }
    for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
      requests.push(this.index(path, { ...params, page: pageNumber }, config))
    }
    const responses = await Promise.all(requests)
    const results = responses.reduce<Snippet[]>(
      (result, response) => [...result, ...response.data],
      []
    )
    return snippetsList.concat(results)
  }

  async getAllSnippets(filterOwners: FilterOwners): Promise<Snippet[]> {
    addBreadcrumb({
      message: 'Scintillas: Getting all pages of Snippets List',
      category: 'request',
    })
    const personalParams = {
      ...this.DEFAULT_PARAMS,
      filter_owners: filterOwners.personalUuids,
    }
    const teamParams = {
      ...this.DEFAULT_PARAMS,
      filter_owners: filterOwners.teamUuids,
    }
    const responses = await Promise.all([
      this.getSnippets(personalParams, this.PERSONAL_SNIPPETS_PATH),
      this.getSnippets(teamParams, this.TEAM_SNIPPETS_PATH),
    ])
    return responses.reduce((result, response) => [...result, ...response], [])
  }

  getSnippetsByType = withAutoCancellation<Snippet[], Type, Query>((type, query, config?) => {
    const finalQuery = {
      ...this.DEFAULT_PARAMS,
      ...query,
    }

    return type === Type.PERSONAL
      ? this.getSnippets(finalQuery, this.PERSONAL_SNIPPETS_PATH, config)
      : this.getSnippets(finalQuery, this.TEAM_SNIPPETS_PATH, config)
  })
}
